import { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import { CircularProgress } from "@mui/material";
import { ApiServices } from "../../utils/ApiService";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export default function LoginFilesPage() {
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [employeeTab, setEmployeeTab] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [changedRow, setChangedRow] = useState(null);
  const [tempAssignTo, setTempAssignTo] = useState("");
  const [originalAssignTo, setOriginalAssignTo] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState({
    file_name: "",
    application_id: "",
    from_date: "",
    to_date: "",
  });

  // Cancel changes
  const handleCancel = () => {
    setModalOpen(false);
    setChangedRow(null);
    setTempAssignTo("");
    setOriginalAssignTo("");
    setSelectedEmployee("");
  };

  const handleChange = (e, params, currentEmpName) => {
    const selectedEmp =
      employeeTab.find((emp) => emp.id === e.target.value).name || "";
    setSelectedEmployee(selectedEmp);
    setChangedRow(params.row.id);
    setTempAssignTo(e.target.value);
    setOriginalAssignTo(currentEmpName);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const res = await ApiServices.put(`file/delete/${id}`, {
        deleted_by: userId,
      });
      if (res?.status === 200) {
        enqueueSnackbar("File Deleted Successfully", { variant: "success" });
        fetchData();
      } else {
        enqueueSnackbar("Failed to delete File.", { variant: "error" });
      }
    } catch (error) {
      const err = error?.response?.data?.message || "Failed to delete File.";
      enqueueSnackbar(err, { variant: "error" });
    } finally {
      setLoading(false);
      fetchData();
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "file_number", headerName: "File Number", flex: 1 },
    { field: "file_name", headerName: "Applicant Name", flex: 1 },
    { field: "application_id", headerName: "Application Id", flex: 1 },
    { field: "bank_name", headerName: "Bank", flex: 1 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "initiated_by",
      headerName: "Assign To",
      flex: 1,
      renderCell: (params) => {
        const assignedToId = params?.row?.assigned_to;

        const currentEmpName =
          employeeTab.find((emp) => emp?.id === assignedToId)?.name || "";

        return (
          <Select
            value={
              employeeTab.some((emp) => emp?.id === assignedToId)
                ? assignedToId
                : ""
            }
            onChange={(e) => handleChange(e, params, currentEmpName)}
            displayEmpty
            fullWidth
            size="small"
          >
            <MenuItem value="" disabled>
              {currentEmpName || "Select Employee"}
            </MenuItem>
            {employeeTab?.map((emp) => (
              <MenuItem key={emp?.id} value={emp?.id} name={emp?.name}>
                {emp?.name}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", width: "100%" }}>
          <IconButton
            color="primary"
            sx={{ flex: isSuperAdmin ? 0 : 1 }}
            onClick={() => navigate(`/edit-file/${params.row.id}`)}
          >
<EditIcon />
          </IconButton>

          {isSuperAdmin && (
            <IconButton
              color="error"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    }
  ];

  const userId = JSON.parse(localStorage.getItem("user"))?.id || "";
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await ApiServices.get(`file/login-files/${userId}`);
      if (res?.status === 200) {
        const apiData = res?.data;
        setRows(apiData?.length ? apiData : null);
      } else {
        console.error("Error fetching files:", res);
      }
    } catch (err) {
      console.error("catch error:", err);
      setRows(null);
    } finally {
      setLoading(false);
    }
  };

  const updateData = async () => {
    try {
      setLoading(true);
      const res = await ApiServices.put(
        `file/${changedRow}/assigned-to/${tempAssignTo}`,
      );
      if (res?.status === 200) {
        fetchData();
        enqueueSnackbar(`File ${changedRow} Update Success !`, {
          variant: "success",
        });
        setModalOpen(false);
        setChangedRow(null);
        setTempAssignTo("");
        setOriginalAssignTo("");
        setSelectedEmployee("");
        //setLoading(false)
      } else {
        enqueueSnackbar("Assigned to file failed.", { variant: "error" });
        console.error("Error fetching files:", res);
      }
    } catch (error) {
      const err = error?.response?.data?.message || "Assigned to file failed.";
      enqueueSnackbar(err, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Save changes from modal
  const handleSave = () => {
    updateData();
  };

  useEffect(() => {
    fetchData();
    const userData = JSON.parse(localStorage.getItem("user"));
    const employee_tab = userData?.employee_tab || "";
    setIsSuperAdmin(userData?.role === "Super Admin" ? true : false);
    setEmployeeTab(employee_tab);
    // console.log(employee_tab)
  }, []);

  const filteredRows = useMemo(() => {
    return rows?.filter((row) => {
      let fileNameMatch = searchTerm?.file_name
        ? row?.file_name
            ?.toLowerCase()
            .includes(searchTerm?.file_name.toLowerCase())
        : true;

      if (!fileNameMatch) {
        fileNameMatch = searchTerm?.file_name
          ? row?.file_number
              ?.toLowerCase()
              .includes(searchTerm?.file_name.toLowerCase())
          : true;
      }

      const appIdMatch = searchTerm?.application_id
        ? row?.application_id
            ?.toLowerCase()
            .includes(searchTerm?.application_id.toLowerCase())
        : true;

      const rowDate = row?.created_at ? new Date(row.created_at) : null;

      const fromDate = searchTerm?.from_date
        ? new Date(searchTerm?.from_date)
        : null;
      const toDate = searchTerm?.to_date ? new Date(searchTerm?.to_date) : null;

      const normalizeStart = (date) => {
        if (!date) return null;
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
      };

      const normalizeEnd = (date) => {
        if (!date) return null;
        const d = new Date(date);
        d.setHours(23, 59, 59, 999);
        return d;
      };

      const normalizedFromDate = normalizeStart(fromDate);
      const normalizedToDate = normalizeEnd(toDate);

      const dateMatch = rowDate
        ? (!normalizedFromDate || rowDate >= normalizedFromDate) &&
          (!normalizedToDate || rowDate <= normalizedToDate)
        : true;

      return fileNameMatch && appIdMatch && dateMatch;
    });
  }, [rows, searchTerm]);

  // console.log("rows----------->>", rows)
  // console.log("changedRow----------->>", changedRow)
  // console.log("tempAssignTo----------->>", tempAssignTo)
  // console.log("originalAssignTo----------->>", originalAssignTo)

  return (
    <>
      <Grid container spacing={3}>
        {/* Filters Section */}
        <Grid item xs={12} style={{ width: "100%" }}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <IconButton onClick={() => setFiltersOpen(!filtersOpen)}>
                {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Grid>

            <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
              <Grid container spacing={2} style={{ marginTop: 16 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Appl. Name or File No"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchTerm.file_name}
                    onChange={(e) => {
                      setSearchTerm({
                        file_name: e.target.value.trim(),
                        application_id: "",
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Application Id"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchTerm.application_id}
                    onChange={(e) => {
                      setSearchTerm({
                        file_name: "",
                        application_id: e.target.value.trim(),
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="From Date"
                    variant="outlined"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={searchTerm.from_date}
                    onChange={(e) =>
                      setSearchTerm((prev) => ({
                        ...prev,
                        from_date: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="To Date"
                    variant="outlined"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={searchTerm.to_date}
                    onChange={(e) =>
                      setSearchTerm((prev) => ({
                        ...prev,
                        to_date: e.target.value,
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Paper>
        </Grid>

        {/* Grid Section */}
        <Grid item xs={12} style={{ width: "100%" }}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography variant="h6" gutterBottom>
                Login Files
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/create-file")}
                >
                  Create File
                </Button>
              </Box>
            </Grid>

            <div style={{ height: 400, width: "100%", marginTop: 20 }}>
              {loading ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  style={{ height: "100%" }}
                >
                  <CircularProgress />
                </Grid>
              ) : (
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  showToolbar
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  onRowDoubleClick={(params) =>
                    navigate(`/edit-file/${params.row.id}`, {
                      state: { file: params.row },
                    })
                  }
                />
              )}
            </div>
          </Paper>
        </Grid>
      </Grid>

      {/* Modal for Save */}
      <Dialog open={modalOpen} onClose={handleCancel}>
        <DialogTitle>Save Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {originalAssignTo ? (
              <>
                <strong> File - {changedRow} </strong> Are you sure change from{" "}
                <strong>{originalAssignTo}</strong> to{" "}
                <strong>{selectedEmployee}</strong>?
              </>
            ) : (
              <>
                {" "}
                <strong> File - {changedRow}</strong>{" "}
                <strong>Are you sure assign to - {selectedEmployee} </strong>?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
